import React from "react";
import { FiX, FiCheckCircle } from "react-icons/fi";
import { formatMoneyByLanguage } from "../../helpers/money";
import { useTranslation } from "react-i18next";

const MAIN_COLOR = "rgb(29, 71, 127)";

const CardPanel = ({
  cart,
  totalAmount,
  cartSummary,
  language = "tr",
  onChangeQuantity,
  mobile,
  onClose,
  onConfirmCart,
  confirmLoading = false,
  confirmError = "",
}) => {
  const { t } = useTranslation();

  const lang = String(language || "tr")
    .toLowerCase()
    .replace("_", "-")
    .trim()
    .slice(0, 2);

  return (
    <div className="flex h-full flex-col overflow-hidden bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4">
        <h3 className="text-base font-semibold text-slate-800">
          {lang === "fr"
            ? "Panier"
            : lang === "de"
              ? "Warenkorb"
              : lang === "nl"
                ? "Winkelwagen"
                : lang === "en"
                  ? "Cart"
                  : "Sepet"}
        </h3>

        {mobile && (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
          >
            <FiX size={18} />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto bg-slate-50 p-3">
        {cart.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            {lang === "fr"
              ? "Votre panier est vide"
              : lang === "de"
                ? "Ihr Warenkorb ist leer"
                : lang === "nl"
                  ? "Uw winkelwagen is leeg"
                  : lang === "en"
                    ? "Your cart is empty"
                    : "Sepetinizde henüz ürün yok"}
          </div>
        ) : (
          <div className="space-y-3">
            {cart.map((item) => (
              <div
                key={item.code || item.id}
                className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-slate-50">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="max-h-14 max-w-14 object-contain"
                      />
                    ) : (
                      <div className="text-[10px] text-slate-400">No Img</div>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="line-clamp-2 text-sm font-semibold text-slate-800">
                      {item.name}
                    </div>

                    <div className="mt-1 text-xs text-slate-500">
                      {formatMoneyByLanguage(item.unitPrice, lang)}
                    </div>

                    <div className="mt-3 flex items-center justify-between gap-3">
                      <div className="flex items-center overflow-hidden rounded-lg border border-slate-300 bg-slate-100">
                        <button
                          onClick={() => onChangeQuantity(item, "decrease")}
                          className="flex h-8 w-8 items-center justify-center font-bold hover:bg-slate-200"
                        >
                          -
                        </button>

                        <div className="w-10 border-x bg-white text-center">
                          {item.quantity}
                        </div>

                        <button
                          onClick={() => onChangeQuantity(item, "increase")}
                          className="flex h-8 w-8 items-center justify-center font-bold hover:bg-slate-200"
                        >
                          +
                        </button>
                      </div>

                      <div className="text-right text-sm font-bold text-slate-800">
                        {formatMoneyByLanguage(
                          item.unitPrice * item.quantity,
                          lang
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-slate-200 bg-white p-4">
        <div className="mb-3 rounded-xl bg-slate-100 px-3 py-3">
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-slate-600">
              {lang === "fr"
                ? "Total (Hors Taxes)"
                : lang === "de"
                  ? "Gesamt (ohne MwSt.)"
                  : lang === "nl"
                    ? "Totaal (excl. btw)"
                    : lang === "en"
                      ? "Total (Excl. VAT)"
                      : "Toplam (KDV hariç)"}
            </span>
            <span className="text-sm font-semibold">
              {formatMoneyByLanguage(cartSummary?.totalExclTax ?? 0, lang)}
            </span>
          </div>

          <div className="flex items-center justify-between border-t py-2">
            <span className="text-sm text-slate-600">
              {lang === "fr"
                ? "Total TVA"
                : lang === "de"
                  ? "MwSt. Gesamt"
                  : lang === "nl"
                    ? "Totaal btw"
                    : lang === "en"
                      ? "Total VAT"
                      : "Toplam KDV"}
            </span>
            <span className="text-sm font-semibold">
              {formatMoneyByLanguage(cartSummary?.totalTax ?? 0, lang)}
            </span>
          </div>

          <div className="flex items-center justify-between border-t pt-2">
            <span className="text-sm font-semibold">
              {lang === "fr"
                ? "Total à Payer"
                : lang === "de"
                  ? "Zu zahlender Gesamtbetrag"
                  : lang === "nl"
                    ? "Totaal te betalen"
                    : lang === "en"
                      ? "Total Payable"
                      : "Ödenecek Toplam"}
            </span>

            <span className="text-base font-bold" style={{ color: MAIN_COLOR }}>
              {formatMoneyByLanguage(
                cartSummary?.totalToPay ?? totalAmount ?? 0,
                lang
              )}
            </span>
          </div>
        </div>

        {confirmError && (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {confirmError}
          </div>
        )}

        <button
          onClick={onConfirmCart}
          disabled={cart.length === 0 || confirmLoading}
          className="flex h-11 w-full items-center justify-center gap-2 rounded-xl font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: MAIN_COLOR }}
        >
          <FiCheckCircle size={18} />
          {confirmLoading
            ? "Loading..."
            : lang === "fr"
              ? "Confirmer le panier"
              : lang === "de"
                ? "Warenkorb bestätigen"
                : lang === "nl"
                  ? "Winkelwagen bevestigen"
                  : lang === "en"
                    ? "Confirm Cart"
                    : "Sepeti Onayla"}
        </button>
      </div>
    </div>
  );
};

export default CardPanel;